import React, { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import CountUp from "react-countup";
import { Eye, DollarSign, ShieldCheck } from "lucide-react";

/**
 * "By the Numbers" scroll-pinned section — 3 sequential scenes.
 *
 * IMPLEMENTATION NOTE
 * Each scene is rendered simultaneously, absolutely positioned in the same
 * spot, with opacity driven directly by useTransform on scrollYProgress.
 * That guarantees there is never a blank frame between scenes (the previous
 * design used AnimatePresence which produced a brief empty window during
 * exit/enter cycles).
 */
export default function ImpactSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Active scene index (drives both the rail dots AND the rendered scene)
  const [activeScene, setActiveScene] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const idx = p < 0.34 ? 0 : p < 0.67 ? 1 : 2;
    if (idx !== activeScene) setActiveScene(idx);
  });

  // Vertical progress rail fill
  const railFill = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const scenes = [
    {
      icon: Eye,
      eyebrow: "Reach at Scale",
      number: 2,
      decimals: 0,
      prefix: "",
      suffix: " Billion",
      headline: (
        <>
          ad impressions
          <br />
          <span className="aurora-text">every month.</span>
        </>
      ),
      body: "Across publishers, exchanges and content verticals. MonetiseUP processes the traffic that moves the modern web.",
      visual: <ImpressionsViz />,
    },
    {
      icon: DollarSign,
      eyebrow: "Revenue Per Mille",
      number: 4.5,
      decimals: 2,
      prefix: "$",
      suffix: "",
      headline: (
        <>
          average eCPM
          <br />
          <span className="aurora-text">paid to publishers.</span>
        </>
      ),
      body: "Header bidding, premium demand, and yield optimisation working in concert to push every CPM higher.",
      visual: <ECPMChart />,
    },
    {
      icon: ShieldCheck,
      eyebrow: "Quality Above All",
      number: 98,
      decimals: 0,
      prefix: "",
      suffix: "%",
      headline: (
        <>
          viewable, fraud-free,
          <br />
          <span className="aurora-text">audited.</span>
        </>
      ),
      body: "Verified by MOAT and IAS. Every impression measured. Every dollar earned by a real human eye.",
      visual: <ViewabilityGauge />,
    },
  ];

  const sc = scenes[activeScene];

  return (
    <section
      ref={sectionRef}
      data-testid="section-impact"
      className="relative"
      style={{ height: "260vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <div className="aurora-orb bg-[#0047FF] w-[900px] h-[900px] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 opacity-20" />
        <div className="aurora-orb bg-[#8A2BE2] w-[500px] h-[500px] -left-32 top-32 opacity-25" />

        <div className="container-x relative z-10 h-full grid grid-cols-[auto_1fr] gap-6 py-24 items-center">
          {/* Vertical progress rail */}
          <div className="hidden md:flex flex-col items-center gap-4 h-[60vh]">
            <div className="relative w-px flex-1 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                style={{ height: railFill }}
                className="absolute top-0 left-0 right-0 bg-gradient-to-b from-[#00F0FF] to-[#0047FF] rounded-full"
              />
            </div>
            <div className="flex flex-col gap-3">
              {scenes.map((_, i) => (
                <div
                  key={i}
                  data-testid={`impact-step-${i}`}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === activeScene
                      ? "bg-[#00F0FF] scale-150 shadow-[0_0_10px_rgba(0,240,255,0.8)]"
                      : i < activeScene
                        ? "bg-[#0047FF]"
                        : "bg-white/15"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Single active scene — switched by activeScene state */}
          <div className="relative h-full">
            <motion.div
              key={activeScene}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              data-testid={`impact-scene-${activeScene}`}
              className="absolute inset-0 grid lg:grid-cols-[1fr_1fr] gap-12 items-center"
            >
              {/* TEXT */}
              <div className="flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-7 self-start">
                  <sc.icon className="w-3.5 h-3.5 text-[#00F0FF]" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/80">
                    {sc.eyebrow}
                  </span>
                </div>

                <div
                  className="font-display font-bold aurora-text leading-[0.95]"
                  style={{ fontSize: "clamp(3.5rem, 10vw, 11rem)" }}
                >
                  <CountUp
                    end={sc.number}
                    decimals={sc.decimals}
                    duration={1.4}
                    prefix={sc.prefix}
                    suffix={sc.suffix}
                    preserveValue
                  />
                </div>

                <div
                  className="font-display text-white/85 mt-3 leading-tight"
                  style={{ fontSize: "clamp(1.25rem, 2.6vw, 2.5rem)" }}
                >
                  {sc.headline}
                </div>

                <p className="text-white/55 mt-6 max-w-md text-base leading-relaxed">
                  {sc.body}
                </p>
              </div>

              {/* VISUAL */}
              <div className="hidden lg:flex items-center justify-center">
                <div className="w-full max-w-[520px]">{sc.visual}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────── Visual components ───────────────────────── */

function ImpressionsViz() {
  const [count, setCount] = useState(1_847_293_481);
  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => c + Math.floor(560 + Math.random() * 220));
    }, 50);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="glass-strong rounded-3xl p-7">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">
            Live impressions today
          </div>
          <div className="font-mono font-bold text-2xl md:text-3xl text-[#00F0FF] tabular-nums mt-1">
            {count.toLocaleString()}
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </span>
      </div>

      <div className="grid grid-cols-12 gap-1.5">
        {Array.from({ length: 96 }).map((_, i) => {
          const delay = (i * 47) % 2000;
          const intensity = (i * 13) % 100;
          return (
            <div
              key={i}
              className="aspect-square rounded-sm"
              style={{
                background: `rgba(0, 240, 255, ${0.1 + (intensity / 100) * 0.5})`,
                animation: `pulse-cell 2.4s ${delay}ms infinite ease-in-out`,
              }}
            />
          );
        })}
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-white/5">
        <RegionStat region="Americas" pct="42%" />
        <RegionStat region="EMEA" pct="34%" />
        <RegionStat region="APAC" pct="24%" />
      </div>

      <style>{`
        @keyframes pulse-cell {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}

function RegionStat({ region, pct }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-white/45">
        {region}
      </div>
      <div className="font-mono text-base font-bold cyan-text">{pct}</div>
    </div>
  );
}

function ECPMChart() {
  const months = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
  const values = [3.2, 3.4, 3.6, 3.7, 3.9, 4.1, 4.0, 4.2, 4.3, 4.4, 4.45, 4.5];
  const maxV = 5;
  const points = values
    .map(
      (v, i) =>
        `${(i / (values.length - 1)) * 100},${100 - (v / maxV) * 100}`,
    )
    .join(" ");

  return (
    <div className="glass-strong rounded-3xl p-7">
      <div className="flex items-center justify-between mb-1">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">
            12-month eCPM trend
          </div>
          <div className="font-display text-2xl md:text-3xl font-bold mt-1">
            <span className="cyan-text">$4.50</span>{" "}
            <span className="text-emerald-400 text-sm font-mono align-middle">
              +41%
            </span>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/45">
          YTD
        </div>
      </div>

      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="w-full h-48 mt-3"
      >
        <defs>
          <linearGradient id="ecpmFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="ecpmStroke" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0047FF" />
            <stop offset="100%" stopColor="#00F0FF" />
          </linearGradient>
        </defs>
        {[0, 25, 50, 75].map((y) => (
          <line
            key={y}
            x1="0"
            x2="100"
            y1={y}
            y2={y}
            stroke="white"
            strokeOpacity="0.05"
            strokeWidth="0.3"
          />
        ))}
        <polygon points={`0,100 ${points} 100,100`} fill="url(#ecpmFill)" />
        <polyline
          points={points}
          fill="none"
          stroke="url(#ecpmStroke)"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx="100"
          cy={100 - (values[values.length - 1] / maxV) * 100}
          r="1.5"
          fill="#00F0FF"
        />
      </svg>

      <div className="grid grid-cols-12 gap-1 text-[9px] text-white/40 text-center mt-1">
        {months.map((m, i) => (
          <span key={i}>{m}</span>
        ))}
      </div>

      <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-3 gap-3">
        <KeyMetric label="Video" value="$18.20" />
        <KeyMetric label="Display" value="$3.40" />
        <KeyMetric label="CTV" value="$32.40" />
      </div>
    </div>
  );
}

function KeyMetric({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-white/45 mb-0.5">
        {label}
      </div>
      <div className="font-mono text-sm font-bold text-white">{value}</div>
    </div>
  );
}

function ViewabilityGauge() {
  const pct = 98;
  const r = 70;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c * 0.75;
  const total = c * 0.75;

  return (
    <div className="glass-strong rounded-3xl p-7 flex flex-col">
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-3">
        Verified viewability
      </div>

      <div className="relative w-full grid place-items-center">
        <svg viewBox="0 0 200 200" className="w-full max-w-[280px]">
          <defs>
            <linearGradient id="gaugeStroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#0047FF" />
              <stop offset="100%" stopColor="#00F0FF" />
            </linearGradient>
          </defs>
          <circle
            cx="100"
            cy="100"
            r={r}
            fill="none"
            stroke="white"
            strokeOpacity="0.08"
            strokeWidth="14"
            strokeDasharray={`${total} ${c - total}`}
            strokeDashoffset={c * 0.125}
            strokeLinecap="round"
            transform="rotate(135 100 100)"
          />
          <circle
            cx="100"
            cy="100"
            r={r}
            fill="none"
            stroke="url(#gaugeStroke)"
            strokeWidth="14"
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={c * 0.125}
            strokeLinecap="round"
            transform="rotate(135 100 100)"
            style={{ filter: "drop-shadow(0 0 12px rgba(0,240,255,0.5))" }}
          />
          <text
            x="100"
            y="100"
            textAnchor="middle"
            dominantBaseline="middle"
            className="font-display font-bold"
            fill="white"
            style={{ fontSize: 44 }}
          >
            98%
          </text>
          <text
            x="100"
            y="128"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(255,255,255,0.55)"
            style={{ fontSize: 10, letterSpacing: "0.25em" }}
          >
            VIEWABLE
          </text>
        </svg>
      </div>

      <div className="grid grid-cols-3 gap-3 pt-5 mt-2 border-t border-white/5">
        <KeyMetric label="IVT Filter" value="0.4%" />
        <KeyMetric label="MOAT" value="✓" />
        <KeyMetric label="IAS" value="✓" />
      </div>
    </div>
  );
}
